import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import {
  disableAnalytics,
  enableAnalytics,
  getStoredConsent,
  setStoredConsent,
  recordConsent,
} from "../analytics/consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(true);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const storedConsent = useMemo(() => getStoredConsent(), []);

  useEffect(() => {
    let isActive = true;

    const init = async () => {
      if (storedConsent?.analytics === "accepted") {
        enableAnalytics();
        setVisible(false);
      } else if (storedConsent?.analytics === "rejected") {
        disableAnalytics();
        setVisible(false);
      } else {
        enableAnalytics();
        setVisible(true);
      }

      if (isActive) {
        setChecking(false);
      }
    };

    init();
    return () => {
      isActive = false;
    };
  }, [storedConsent]);

  useEffect(() => {
    const handleOpen = () => {
      setChecking(false);
      setPreferencesOpen(true);
    };
    window.addEventListener("cookie-preferences", handleOpen);
    return () => {
      window.removeEventListener("cookie-preferences", handleOpen);
    };
  }, []);

  if ((!visible && !preferencesOpen) || checking) return null;

  const handleContinue = async () => {
    setStoredConsent("accepted");
    enableAnalytics();
    setVisible(false);
    await recordConsent("accepted", "banner", null);
  };

  const handleAccept = async () => {
    setStoredConsent("accepted");
    enableAnalytics();
    setPreferencesOpen(false);
    await recordConsent("accepted", "preferences", null);
  };

  const handleReject = async () => {
    setStoredConsent("rejected");
    disableAnalytics();
    setPreferencesOpen(false);
    await recordConsent("rejected", "preferences", null);
  };

  return (
    <>
      {visible ? (
        <Box
          role="dialog"
          aria-live="polite"
          aria-label="Cookie notice"
          sx={{
            position: "fixed",
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 1400,
            maxWidth: 1020,
            margin: "0 auto",
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: 6,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 70%" } }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Cookies and analytics
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              We use cookies to operate our site and analyze usage to improve our services. You can manage your
              preferences at any time in Cookie Settings.
            </Typography>
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "0 0 30%" },
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-start" },
              }}
            >
            <Button variant="outlined" onClick={() => setPreferencesOpen(true)}>
              Cookie Preferences
            </Button>
            <Button variant="contained" onClick={handleContinue}>
              Continue
            </Button>
            </Box>
          </Box>
        </Box>
      ) : null}
      <Dialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        aria-labelledby="cookie-preferences-title"
      >
        <DialogTitle id="cookie-preferences-title">Cookie settings</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Essential cookies are required for sign-in and security. Analytics cookies help us improve ResourceNAV.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={handleReject}>
            Disable analytics
          </Button>
          <Button variant="contained" onClick={handleAccept}>
            Allow analytics
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
